/**
 * Turn a picked image file into a small square data URL for upload: the whole
 * image is fitted (not cropped) into a size x size square on a transparent
 * background, so a logo keeps its shape. WebP when the browser can make it,
 * otherwise PNG.
 */
export const MAX_IMAGE_FILE_BYTES = 8 * 1024 * 1024;
export const IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file could not be read as an image"));
    };
    img.src = url;
  });

export const imageFileProblem = (file) => {
  if (!file) return "Choose an image file";
  if (!IMAGE_TYPES.includes(file.type)) return "Choose a PNG, JPEG, WebP or GIF image";
  if (file.size > MAX_IMAGE_FILE_BYTES) return "That image is over 8 MB; choose a smaller one";
  return null;
};

export const resizeImageToSquare = async (file, size = 256) => {
  const problem = imageFileProblem(file);
  if (problem) throw new Error(problem);
  const img = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const scale = Math.min(size / img.width, size / img.height);
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, Math.round((size - w) / 2), Math.round((size - h) / 2), w, h);
  const webp = canvas.toDataURL("image/webp", 0.9);
  return webp.startsWith("data:image/webp") ? webp : canvas.toDataURL("image/png");
};
